import { ReactElement } from "react";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { MainLayout } from "~/components/layouts/MainLayout";
import { StandardServicePage } from "~/components/service-pages/StandardServicePage";
import { GasBoilerContent as content } from "~/content/przeglad-kotla-gazowego/content.pl";
import type { City } from "~/lib/cities";
import { getCitiesWithServiceOffers } from "~/lib/firms.server";
import { getRecentPostsForService, type PostMeta } from "~/lib/mdx";
import { getServiceBySlug } from "~/lib/services";
import { NextPageWithLayout } from "~/pages/_app";
import gasDetailImage from "~/src/assets/images/gas_10.jpg";
import gasChecklistImage from "~/src/assets/images/plumbing_1.webp";
import gasServiceImage from "~/src/assets/images/services-details-gas.webp";

type Props = {
  recentPosts: PostMeta[];
  availableCities: City[];
};

const serviceSlug = "przeglad-kotla-gazowego";
const service = getServiceBySlug(serviceSlug);

if (!service) {
  throw new Error(`Service not found: ${serviceSlug}`);
}

const pageUrl = `${(
  process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl"
).replace(/\/+$/, "")}${service.href}`;

const sectionImages = [
  {
    src: gasServiceImage,
    alt: "Technik serwisujący kocioł gazowy",
  },
  {
    src: gasDetailImage,
    alt: "Przegląd palnika i wymiennika ciepła kotła gazowego",
  },
  {
    src: gasChecklistImage,
    alt: "Protokół z przeglądu serwisowego kotła gazowego",
  },
] as const;

const GasBoilerPage: NextPageWithLayout<Props> = ({
  recentPosts,
  availableCities,
}) => {
  return (
    <StandardServicePage
      serviceSlug={serviceSlug}
      content={content}
      sectionImages={sectionImages}
      recentPosts={recentPosts}
      availableCities={availableCities}
    />
  );
};

GasBoilerPage.getLayout = function GetLayout(page: ReactElement) {
  return (
    <MainLayout>
      <NextSeo
        title={content.seo.title}
        description={content.seo.description}
        openGraph={{
          title: content.seo.title,
          description: content.seo.description,
          url: pageUrl,
        }}
      />
      {page}
    </MainLayout>
  );
};

export default GasBoilerPage;

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      recentPosts: getRecentPostsForService(serviceSlug),
      availableCities: getCitiesWithServiceOffers(serviceSlug),
    },
  };
};
